import React, { useState, useEffect, useRef } from "react";
import StyledSection from "../layout/StyledSection";
import ActionCardView from "../cards/ActionCardView";

const YourHandRow = ({ 
  playerActionCards, 
  newActionCardIds, 
  setNewActionCardIds, 
  handleSelectActionCard, 
  myTurn, 
  actionsRemaining, 
  name, 
  selectedActionCard, 
  styleCardSize,
  maxActionCards = 6,
  redrawMode = false,
  selectedForDiscard = new Set(),
  hasUsedRedrawThisTurn = false,
  onEnterRedrawMode,
  onToggleDiscardSelection,
  onCancelRedraw,
  onConfirmRedraw
}) => {
  const [positionStyle, setPositionStyle] = useState({});

  useEffect(() => {
    const updatePosition = () => {
      const gameColumn2 = document.querySelector('.game-column-2');
      if (gameColumn2) {
        const column2Rect = gameColumn2.getBoundingClientRect();
        setPositionStyle({
          left: `${column2Rect.left}px`,
          width: `${column2Rect.width}px`
        });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    
    const gameColumn2 = document.querySelector('.game-column-2');
    let resizeObserver;
    if (gameColumn2 && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(gameColumn2);
    }

    const interval = setInterval(updatePosition, 500);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      clearInterval(interval);
    };
  }, []);

  const processedCardIdsRef = useRef(new Set());
  
  useEffect(() => {
    if (newActionCardIds.size > 0 && setNewActionCardIds) {
      const timers = [];
      
      newActionCardIds.forEach(cardId => {
        if (!processedCardIdsRef.current.has(cardId)) {
          processedCardIdsRef.current.add(cardId);
          
          const timer = setTimeout(() => {
            setNewActionCardIds(prev => {
              const updated = new Set(prev);
              updated.delete(cardId);
              return updated;
            });
            processedCardIdsRef.current.delete(cardId);
          }, 2000);
          timers.push(timer);
        }
      });
      
      processedCardIdsRef.current.forEach(processedId => {
        if (!newActionCardIds.has(processedId)) {
          processedCardIdsRef.current.delete(processedId);
        }
      });
      
      return () => {
        timers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [newActionCardIds, setNewActionCardIds]);

  const canRedraw = myTurn && (actionsRemaining[name] || 0) > 0 && !hasUsedRedrawThisTurn && playerActionCards.length > 0;

  return (
    <div 
      className="game-column-2-row game-hand-row"
      style={positionStyle}
      data-tutorial="your-hand"
    >
      <StyledSection
        title={redrawMode ? "Select Cards to Discard" : "Your Hand"}
        icon={redrawMode ? "🔄" : "🃏"}
      >
        {/* Redraw controls */}
        {myTurn && (
          <div className="hand-redraw-controls">
            {!redrawMode ? (
              <button
                className="hand-redraw-button"
                onClick={onEnterRedrawMode}
                disabled={!canRedraw}
                title={hasUsedRedrawThisTurn ? "Already used this turn" : "Spend 1 action to discard 1-3 cards and draw replacements"}
              >
                🔄 Discard & Redraw
              </button>
            ) : (
              <div className="hand-redraw-active-controls">
                <span className="hand-redraw-count">
                  {selectedForDiscard.size}/3 selected
                </span>
                <button
                  className="hand-redraw-confirm"
                  onClick={onConfirmRedraw}
                  disabled={selectedForDiscard.size === 0}
                >
                  ✓ Confirm ({selectedForDiscard.size})
                </button>
                <button
                  className="hand-redraw-cancel"
                  onClick={onCancelRedraw}
                >
                  ✕ Cancel
                </button>
              </div>
            )}
          </div>
        )}
        <div className="flex gap-sm flex-wrap justify-center">
          {playerActionCards.map((actionCard, idx) => (
            <ActionCardView
              key={actionCard.id || idx}
              action={actionCard}
              styleCardSize={styleCardSize}
              onSelect={redrawMode ? onToggleDiscardSelection : handleSelectActionCard}
              canPlay={redrawMode ? true : (myTurn && (actionsRemaining[name] || 0) > 0)}
              isSelected={redrawMode ? selectedForDiscard.has(actionCard.id) : selectedActionCard?.id === actionCard.id}
              isNew={actionCard.id && newActionCardIds.has(actionCard.id)}
              isDiscardMode={redrawMode}
            />
          ))}
          {!redrawMode && Array.from({ length: Math.max(0, maxActionCards - playerActionCards.length) }).map((_, idx) => (
            <div
              key={`placeholder-${idx}`}
              className="action-card-container"
            >
              <div
                className="stock-card card action-card-placeholder"
                style={styleCardSize}
              >
                <div className="stock-card-top action-card-placeholder-header">
                  <span className="action-card-placeholder-emoji">🎴</span>
                  <span className="action-card-placeholder-text">Empty Slot</span>
                </div>
                <div className="stock-card-spacer action-card-placeholder-content">
                  <span className="action-card-placeholder-icon">🎴</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </StyledSection>
    </div>
  );
};

export default YourHandRow;
